import React, { useMemo, useState } from 'react';
import { SmilePlusIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { useConversation } from '../../context';
import type { ReactableMessage } from '../../types';
import { getEmojiImageUrl, groupReactions } from '../../utils';
import { NonParticipantTooltip } from '../NonParticipantTooltip';
import { ReactionPicker } from './ReactionPicker';

/**
 * Displays grouped emoji reactions below a message bubble.
 * @see {@link ChatMessageBubble}
 */
export function ChatMessageReactions({
    className,
    message,
    isOutgoing,
    onReact,
}: {
    className?: string;
    message: ReactableMessage;
    isOutgoing: boolean;
    onReact?: (message: ReactableMessage, reaction: string) => void;
}) {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const { selfParticipant, isParticipant } = useConversation();
    const groupedReactions = useMemo(() => {
        return groupReactions(message.Reactions, selfParticipant);
    }, [message.Reactions, selfParticipant]);

    return (
        <div
            className={cn(
                'flex flex-wrap content-start items-center gap-[4px]',
                isOutgoing ? 'justify-end' : 'justify-start',
                className,
            )}
        >
            {groupedReactions.map((reaction) => (
                <TooltipProvider key={reaction.emoji} delayDuration={500}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <button
                                    type="button"
                                    className={cn(
                                        'flex h-[24px] items-center justify-center gap-[5px] rounded-full border px-[6px] transition-colors disabled:pointer-events-none disabled:opacity-50',
                                        reaction.hasReacted
                                            ? 'border-brick-900 text-brick-900 hover:bg-brick-100/10'
                                            : 'border-gray-400 text-gray-700 hover:bg-white/20',
                                    )}
                                    disabled={!isParticipant}
                                    onClick={() =>
                                        onReact?.(message, reaction.emoji)
                                    }
                                >
                                    <Emoji emoji={reaction.emoji} />
                                    <span className="text-[11px] leading-none font-semibold tabular-nums">
                                        {reaction.count}
                                    </span>
                                </button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent
                            className="max-w-[200px] wrap-break-word"
                            side="top"
                        >
                            {reaction.participantNames.join(', ')}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ))}
            {isParticipant ? (
                <ReactionPicker
                    open={isPickerOpen}
                    onOpenChange={setIsPickerOpen}
                    onReact={(reaction) => onReact?.(message, reaction)}
                >
                    <ReactionPickerButton />
                </ReactionPicker>
            ) : (
                <NonParticipantTooltip asChild>
                    <span className="inline-flex">
                        <ReactionPickerButton disabled />
                    </span>
                </NonParticipantTooltip>
            )}
        </div>
    );
}

/**
 * The component that displays the emoji reaction. Renders an image and fallbacks
 * to the Unicode emoji if the image is not available.
 */
function Emoji({ emoji }: { emoji: string }) {
    const url = getEmojiImageUrl(emoji);

    return url ? (
        <img className="size-[16px]" src={url} alt={emoji} />
    ) : (
        <span className="text-[16px] leading-none">{emoji}</span>
    );
}

const ReactionPickerButton = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'>
>(function ReactionPickerButton({ className, ...props }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                'grid h-[24px] w-[28px] place-items-center rounded-full border border-gray-300 bg-white/40 text-gray-600 transition-colors hover:border-gray-400 hover:bg-white/30 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50',
                className,
            )}
            aria-label="Add reaction"
            {...props}
        >
            <SmilePlusIcon className="size-[14px]" />
        </button>
    );
});
